import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconEdit,
  IconPlus,
  IconHistoryToggle,
  IconBell,
  IconPaw,
} from "@tabler/icons-react";

// Hooks and Services
import { getMemberInfo } from "@/hooks/useMembers";
import usePetCare from "@/hooks/usePetCare";

// Types
import { PetCareType, PetCare as IPetCare } from "@/types/petCare";

// Custom Components
import FormHeader from "@/components/ui/organisms/MHeader";
import { getMemberName } from "@/utils/member";
import { ListItem } from "@/components/ui/atoms/MListItem";
import { useNavigate } from "react-router-dom";
import { appendToPath } from "@/utils/nav-utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MLoader from "@/components/ui/atoms/MLoader";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { MButton } from "@/components/ui/atoms/MButtons";
import { IconFrequency } from "@/components/icons";
import {
  getPetCareFrequencyText,
  getPetCareReminderDateText,
} from "@/utils/petcare-utils";
import styles from "./pet-care.module.css";

const PetCare = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const name = getMemberName(memberInfo);
  const [loading, setLoading] = useState(true);

  const { deworming, fleaTreatment, fetchPetCare } = usePetCare(memberId);

  // Fetch pet care data on component mount
  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        await fetchPetCare();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (memberId) {
      fetchData();
    }
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const handleAddUpdatePetCare = (type: PetCareType) => {
    navigate(appendToPath(location.pathname, "add-update-schedule"), {
      state: {
        petCareType: type,
        petCare: type == PetCareType.DEWORMING ? deworming : fleaTreatment,
      },
    });
  };

  const handleShowHistory = (type: PetCareType) => {
    navigate(appendToPath(location.pathname, "history"), {
      state: {
        petCareType: type,
      },
    });
  };

  const getActions = (petCare: IPetCare) => {
    const type = petCare.type;
    return [
      {
        id: "edit",
        label: t("common.edit"),
        icon: <IconEdit size={20} />,
        onClick: () => handleAddUpdatePetCare(type),
        isVisible: true,
      },
      {
        id: "show-history",
        label: t("petcare.showHistory"),
        icon: <IconHistoryToggle size={20} />,
        onClick: () => handleShowHistory(type),
        isVisible: true,
      },
    ];
  };

  if (loading) {
    return <MLoader />;
  }

  return (
    <div className="profile-page-root">
      <FormHeader
        module="petcare"
        memberInfo={memberInfo}
        title={t("petcare.title", { name })}
        description={t("petcare.description", { name })}
        learnMoreContent={t("petcare.learnMoreContent")}
        showGradient={true}
      />
      <MSpacer mt={24} />

      {!deworming && !fleaTreatment ? (
        <MEmptyCard
          icon={<IconPaw />}
          primaryTitle={t("petcare.no-petcare")}
          primaryCTA={{
            text: t("petcare.deworming"),
            onClick: () => handleAddUpdatePetCare(PetCareType.DEWORMING),
          }}
          secondaryCTA={{
            text: t("petcare.fleaTickControl"),
            onClick: () => handleAddUpdatePetCare(PetCareType.FLEA_CONTROL),
            variant: "filled",
          }}
        />
      ) : (
        <div className={styles.careContainer}>
          {/* Deworming Card */}
          {deworming ? (
            <ListItem
              title={t("petcare.deworming")}
              emphasizedText={getPetCareFrequencyText(deworming, t)}
              emphasizedIcon={<IconFrequency size={12} color="#2A2A2A" />}
              tertiaryText={getPetCareReminderDateText(deworming)}
              tertiaryIcon={<IconBell size={16} />}
              tertiaryVariant="orange"
              icon={<IconPaw />}
              iconPosition="offset"
              variant="purple"
              type="3-line"
              usage="crud"
              actions={getActions(deworming)}
            />
          ) : (
            <MButton
              text={t("petcare.deworming")}
              leftIcon={<IconPlus />}
              variant="outline"
              size="md"
              className="w-full"
              onClick={() => handleAddUpdatePetCare(PetCareType.DEWORMING)}
            />
          )}

          {/* Flea & Tick Control Card */}
          {fleaTreatment ? (
            <ListItem
              title={t("petcare.fleaTickControl")}
              emphasizedText={getPetCareFrequencyText(fleaTreatment, t)}
              emphasizedIcon={<IconFrequency size={12} color="#2A2A2A" />}
              tertiaryText={getPetCareReminderDateText(fleaTreatment)}
              tertiaryIcon={<IconBell size={16} />}
              tertiaryVariant="orange"
              icon={<IconPaw />}
              iconPosition="offset"
              type="3-line"
              usage="crud"
              actions={getActions(fleaTreatment)}
              variant="purple"
            />
          ) : (
            <MButton
              text={t("petcare.fleaTickControl")}
              leftIcon={<IconPlus />}
              variant="outline"
              size="md"
              className="w-full"
              onClick={() => handleAddUpdatePetCare(PetCareType.FLEA_CONTROL)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PetCare;
